import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Analytics } from "@biocentury/core";
import { Product } from "@biocentury/core/types";
import ExportCompanyProfile from "@components/export-company-profile/export-profile";
import ExportProfile from "@components/export-profile/export-profile";
import GuestMessage from "@components/guest-message";
import Loading from "@components/loading";
import ProfileTabs from "@components/profile-tabs";
import Search from "@components/search";
import TrialMessage from "@components/trial-message";
import AuthContext, { RoleType } from "@contexts/auth-context";
import { getProfileTypesData, profileTypesData } from "@utils/helpers/profile-data";
import { compact } from "lodash";
import { NextSeo } from "next-seo";
import Head from "next/head";
import React, { useContext, useEffect, useState } from "react";
var ProfileContainer = function(param) {
    var type = param.type, guestData = param.guestData, isResultPage = param.isResultPage, profileData = param.profileData, tombstoneComponents = param.tombstoneComponents;
    var appUrl = process.env.NEXT_PUBLIC_APP_URL;
    var currentUser = useContext(AuthContext);
    var isBCIQOrAdmin = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.customClaims.isBCIQ) || (currentUser === null || currentUser === void 0 ? void 0 : currentUser.customClaims.roles.includes(RoleType.Admin));
    var ref = useState(null), profile = ref[0], setProfile = ref[1];
    var ref1 = useState(null), printReportConfig = ref1[0], setPrintReportConfig = ref1[1];
    var profileName = type === "targets" && (profile === null || profile === void 0 ? void 0 : profile.dimension) !== "moleculartargetfamily" ? profile === null || profile === void 0 ? void 0 : profile.legacyTargetName : profile === null || profile === void 0 ? void 0 : profile.completeName;
    var pageTitle = compact([
        (profileData === null || profileData === void 0 ? void 0 : profileData.legacyTargetName) || (profileData === null || profileData === void 0 ? void 0 : profileData.completeName) || null,
        "BioCentury ".concat(profileTypesData[type].titlePage),
        "BCIQ", 
    ]).join(" - ");
    var pagePath = isResultPage ? "/".concat(type, "/").concat(profileData === null || profileData === void 0 ? void 0 : profileData.encodedName) : "/".concat(type);
    var pageFullPath = "".concat(appUrl).concat(pagePath);
    var pageDescription = compact([
        (profileData === null || profileData === void 0 ? void 0 : profileData.legacyTargetName) || (profileData === null || profileData === void 0 ? void 0 : profileData.completeName) || null,
        "BioCentury ".concat(profileTypesData[type].titlePage, " for the biopharma industry"), 
    ]).join(" - ");
    useEffect(function() {
        if (profileData) {
            setProfile(profileData);
        }
    }, [
        appUrl,
        profileData,
        type
    ]);
    useEffect(function() {
        var event = {
            name: "pageview",
            action: "pageview",
            path: pagePath,
            category: pagePath,
            product: Product.Profiles
        };
        Analytics.WebUsage.track(event);
        Analytics.GoogleAnalytics.track(Object.assign(Object.assign({}, event), {
            category: "profile-" + type
        }));
    }, []);
    useEffect(function() {
        var newProfileTypesData = getProfileTypesData({
            type: type,
            archived: profile === null || profile === void 0 ? void 0 : profile.archived,
            companyType: profile === null || profile === void 0 ? void 0 : profile.companyType,
            isLoggedIn: !!currentUser,
            dimension: profile === null || profile === void 0 ? void 0 : profile.dimension
        });
        setPrintReportConfig({
            type: type,
            showExportButton: newProfileTypesData.showExportButton,
            workspaceId: newProfileTypesData.workspaceId,
            reportId: newProfileTypesData.printReportId,
            pages: newProfileTypesData.printPages,
            fileName: "".concat(newProfileTypesData.titlePage, " - ").concat(profileName, ".pdf"),
            profileName: profileName === null || profileName === void 0 ? void 0 : profileName.replace(/'/g, "''"),
            filter: newProfileTypesData.printFilter,
            dimension: profile === null || profile === void 0 ? void 0 : profile.dimension
        });
    }, [
        currentUser,
        profile,
        profileName,
        type
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Head, {
                children: /*#__PURE__*/ _jsx("script", {
                    type: "application/ld+json",
                    dangerouslySetInnerHTML: {
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Dataset",
                            name: pageTitle,
                            description: pageDescription,
                            url: pageFullPath,
                            creator: {
                                "@type": "Organization",
                                url: "https://biocentury.com",
                                name: "Biocentury",
                                contactPoint: {
                                    "@type": "ContactPoint",
                                    contactType: "customer service",
                                    telephone: "+1 650-552-4224",
                                    email: "support@biocentury.com"
                                }
                            },
                            founder: {
                                "@type": "Organization",
                                sameAs: "https://biocentury.com",
                                name: "Biocentury"
                            }
                        })
                    }
                })
            }),
            /*#__PURE__*/ _jsx(NextSeo, {
                title: pageTitle,
                defaultTitle: "BioCentury",
                description: pageDescription,
                canonical: pageFullPath,
                openGraph: {
                    site_name: "BioCentury",
                    type: "website",
                    url: pageFullPath,
                    title: pageTitle,
                    description: pageDescription
                },
                twitter: {
                    cardType: "summary_large_image"
                },
                additionalMetaTags: [
                    {
                        name: "twitter:url",
                        content: pageFullPath
                    },
                    {
                        name: "twitter:site",
                        content: "@biocentury"
                    },
                    {
                        name: "twitter:title",
                        content: pageTitle
                    },
                    {
                        name: "twitter:description",
                        content: pageDescription
                    },
                    {
                        name: "twitter:image",
                        content: "".concat(process.env.NEXT_PUBLIC_APP_URL, "/favicon.png")
                    },
                    {
                        name: "twitter:image:alt",
                        content: "BCIQ"
                    }, 
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mx-4 mt-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "lg:flex justify-between items-center",
                        children: [
                            /*#__PURE__*/ _jsx(Search, {
                                defaultValue: profile === null || profile === void 0 ? void 0 : profile[profileTypesData[type].completeNameKey],
                                type: type
                            }),
                            !isBCIQOrAdmin && guestData && (profile === null || profile === void 0 ? void 0 : profile.encodedName) && /*#__PURE__*/ _jsx(TrialMessage, {
                                data: guestData
                            }),
                            isBCIQOrAdmin && (profile === null || profile === void 0 ? void 0 : profile.encodedName) && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    [
                                        "companies"
                                    ].includes(type) && /*#__PURE__*/ _jsx(ExportCompanyProfile, {
                                        printReportConfig: printReportConfig
                                    }),
                                    [
                                        "targets",
                                        "diseases",
                                        "products"
                                    ].includes(type) && /*#__PURE__*/ _jsx(ExportProfile, {
                                        printReportConfig: printReportConfig
                                    })
                                ]
                            })
                        ]
                    }),
                    !isBCIQOrAdmin && guestData && !(profile === null || profile === void 0 ? void 0 : profile.encodedName) && !isResultPage && /*#__PURE__*/ _jsx(GuestMessage, {
                        data: guestData
                    })
                ]
            }),
            isResultPage && !profile && /*#__PURE__*/ _jsx(Loading, {}),
            (profile === null || profile === void 0 ? void 0 : profile.encodedName) && /*#__PURE__*/ _jsx(ProfileTabs, {
                encodedName: profile === null || profile === void 0 ? void 0 : profile.encodedName,
                guestData: guestData,
                profileData: profile,
                profileName: profileName,
                tombstoneComponents: tombstoneComponents,
                type: type
            })
        ]
    });
};
export default ProfileContainer;
